import React, { FC, useContext, useEffect, useRef, useState } from "react";
import { Button, DatePicker, Select, Table } from "antd";
import type { ColumnsType, TablePaginationConfig } from "antd/es/table";
import { useMutation } from "react-query";
import { Dayjs } from "dayjs";
import GlobalContext from "src/context/global-context";
import {
  APISomethingWrongMsg,
  NetworkDisconnectMsg,
  permissions as permissionConstants,
} from "src/lib/constants";
import usePermissions from "src/hooks/use-permissions";
import { getErrorMessage } from "src/lib/utils";
import client from "../../api";

const { RangePicker } = DatePicker;

interface MtsTransferLogUser {
  id: number;
  email: string;
  createdAt: string;
}

interface MtsTransferLogAsset {
  id: number;
  name: string;
  code: string;
  link: string;
  depositable: boolean;
  withdrawable: boolean;
  conversible: boolean;
}

interface MtsTransferLogHistory {
  id: number;
  wallet_terminal_id: number;
  user_id: number;
  provider_identifier: string;
  asset_id: number;
  amount: string;
  fee: string;
  total: string;
  provider_transaction_ref: string | null;
  transfer_type: string | null;
  idempotency_key: string | null;
  source: string | null;
  type: string | null;
  status: string | null;
  description: string | null;
  canceled_reason: string | null;
  mts_request_id: string | null;
  mts_ledger_id: string | null;
  mts_status_code: number | null;
  mts_status_name: string | null;
  mts_last_checked_at: string | null;
  mts_refund_status: string | null;
  mts_refund_request_id: string | null;
  mts_refund_attempt: number | null;
  created_at: string;
  updated_at: string;
}

interface MtsRequestPayloadClient {
  refcode: string;
  lastName: string;
  firstName: string;
  personalNumber: string;
}

interface MtsRequestPayload {
  amount: number;
  client: MtsRequestPayloadClient;
  currency: string;
  requestId: string;
  receiverCurrency: string;
}

interface MtsResponseStatus {
  code: number;
  name: string;
}

interface MtsResponseData {
  status: MtsResponseStatus;
  ledgerId: number | string;
  requestId: string;
}

interface MtsTransferLog {
  id: number;
  wallet_terminal_history_id: number;
  user_id: number;
  payment_type: string;
  request_id: string;
  attempt: number;
  status: string;
  transfer_status: string | null;
  mts_ledger_id: string | null;
  amount: string;
  sender_currency: string;
  receiver_currency: string;
  asset_id: number;
  request_payload: MtsRequestPayload;
  response_data: MtsResponseData | null;
  error_message: string | null;
  http_status_code: number | null;
  created_at: string;
  updated_at: string;
  user: MtsTransferLogUser | null;
  asset: MtsTransferLogAsset | null;
  history: MtsTransferLogHistory | null;
}

interface MtsTransferLogsResponse {
  status: string;
  code: number;
  message: string;
  data: Record<string, unknown>;
  items: MtsTransferLog[];
  totalcount: number;
  page?: number;
  limit?: number | null;
  totalPages?: number | null;
}

type TransferStatus =
  | "Initialized"
  | "AmlFiltering"
  | "LimitApprovement"
  | "ReadyToTransfer"
  | "InitTransfer"
  | "BlackListFiltering"
  | "InProgress"
  | "Accepted"
  | "Rejected"
  | "TransactionError";

interface GetMtsTransferLogsParams {
  page: number;
  limit: number;

  user_id?: number;
  wallet_terminal_history_id?: number;
  payment_type?: string;
  status?: TransferStatus;
  request_id?: string;

  start_date?: string;
  end_date?: string;
}

type MtsTransferFilter =
  | "user_id"
  | "wallet_terminal_history_id"
  | "payment_type"
  | "status"
  | "request_id";
interface MtsTransferFilterOption {
  value: MtsTransferFilter;
  label: string;
}

const mtsTransferFilterOptions: MtsTransferFilterOption[] = [
  {
    value: "user_id",
    label: "User ID",
  },
  {
    value: "wallet_terminal_history_id",
    label: "Wallet Terminal History ID",
  },
  {
    value: "payment_type",
    label: "Payment Type",
  },
  {
    value: "status",
    label: "Transfer Status",
  },
  {
    value: "request_id",
    label: "Request ID",
  },
];

const transferStatusOptions: {
  value: TransferStatus;
  label: string;
}[] = [
  {
    value: "Initialized",
    label: "Initialized",
  },
  {
    value: "AmlFiltering",
    label: "AML Filtering",
  },
  {
    value: "LimitApprovement",
    label: "Limit Approvement",
  },
  {
    value: "ReadyToTransfer",
    label: "Ready To Transfer",
  },
  {
    value: "InitTransfer",
    label: "Init Transfer",
  },
  {
    value: "BlackListFiltering",
    label: "Black List Filtering",
  },
  {
    value: "InProgress",
    label: "In Progress",
  },
  {
    value: "Accepted",
    label: "Accepted",
  },
  {
    value: "Rejected",
    label: "Rejected",
  },
  {
    value: "TransactionError",
    label: "Transaction Error",
  },
];

type DateRangeValue = [Dayjs | null, Dayjs | null] | null;

const MtsTransferLogs: FC = () => {
  const { canView, canList } = usePermissions(
    permissionConstants.TRANSACTIONS
  );
  const canViewMtsLogs = canView || canList;

  const [dataSource, setDataSource] = useState<MtsTransferLog[]>([]);
  const [totalCount, setTotalCount] = useState<number>(0);

  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
  });

  const [selectedFilter, setSelectedFilter] =
    useState<MtsTransferFilter>("user_id");

  const [filterValue, setFilterValue] = useState("");
  const [dateRange, setDateRange] = useState<DateRangeValue>(null);
  const debounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const { setIsAlertOpened, setAlertText } = useContext(GlobalContext);

  /* ---------------- ACTIVITY LOGS ---------------- */

  const { mutate: getMtsTransferLogs, isLoading } = useMutation<
    MtsTransferLogsResponse,
    unknown,
    GetMtsTransferLogsParams
  >(client.newTransactionsHistory.getMtsTransferLogs, {
    onSuccess: (data) => {
      setDataSource(data.items || []);
      setTotalCount(data.totalcount || 0);

      if (data.message === "You do not have access for this route!") {
        setAlertText(data.message);
        setIsAlertOpened(true);
      }
    },

    onError: (error: unknown) => {
      const apiError = error as {
        code?: string;
        response?: {
          data: unknown;
        };
      };

      if (apiError.code === "ERR_NETWORK") {
        setAlertText(NetworkDisconnectMsg);
      } else if (apiError.response) {
        setAlertText(getErrorMessage(apiError.response.data));
      } else {
        setAlertText(APISomethingWrongMsg);
      }

      setIsAlertOpened(true);
    },
  });

  /* ---------------- FETCH LOGS ---------------- */

  const fetchMtsTransferLogs = (
    page = pagination.current,
    limit = pagination.pageSize,
    dates = dateRange,
    filter = selectedFilter,
    value = filterValue,
  ) => {
    if (!canViewMtsLogs) {
      return;
    }
    const params: GetMtsTransferLogsParams = {
      page,
      limit,
    };

    if (value.trim()) {
      switch (filter) {
        case "user_id": {
          const userId = Number(value);

          if (!Number.isNaN(userId)) {
            params.user_id = userId;
          }

          break;
        }

        case "wallet_terminal_history_id": {
          const historyId = Number(value);

          if (!Number.isNaN(historyId)) {
            params.wallet_terminal_history_id = historyId;
          }

          break;
        }

        case "payment_type":
          params.payment_type = value;
          break;

        case "status":
          params.status = value as TransferStatus;
          break;

        case "request_id":
          params.request_id = value;
          break;
      }
    }

    if (dates?.[0]) {
      params.start_date = dates[0].format("YYYY-MM-DD");
    }

    if (dates?.[1]) {
      params.end_date = dates[1].format("YYYY-MM-DD");
    }

    getMtsTransferLogs(params);
  };

  /* ---------------- INITIAL LOAD ---------------- */

  useEffect(() => {
    if (canViewMtsLogs) {
      fetchMtsTransferLogs(1, 10);
    }
  }, [canViewMtsLogs]);

  /* ---------------- ACTION FILTER ---------------- */

  const handleFilterChange = (value: MtsTransferFilter) => {
    setSelectedFilter(value);
    setFilterValue("");

    setPagination((previous) => ({
      ...previous,
      current: 1,
    }));
  };

  const handleFilterValueChange = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const value = event.target.value;

    setFilterValue(value);

    setPagination((previous) => ({
      ...previous,
      current: 1,
    }));

    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    debounceTimerRef.current = setTimeout(() => {
      fetchMtsTransferLogs(
        1,
        pagination.pageSize,
        dateRange,
        selectedFilter,
        value,
      );
    }, 500);
  };

  const handleStatusChange = (value: TransferStatus | undefined) => {
    const statusValue = value || "";

    setFilterValue(statusValue);

    setPagination((previous) => ({
      ...previous,
      current: 1,
    }));

    fetchMtsTransferLogs(
      1,
      pagination.pageSize,
      dateRange,
      selectedFilter,
      statusValue,
    );
  };

  const handleClearFilters = () => {
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    const initialFilter: MtsTransferFilter = "user_id";

    setSelectedFilter(initialFilter);
    setFilterValue("");
    setDateRange(null);

    setPagination({
      current: 1,
      pageSize: 10,
    });

    fetchMtsTransferLogs(1, 10, null, initialFilter, "");
  };

  useEffect(() => {
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, []);

  /* ---------------- DATE FILTER ---------------- */

  const handleDateRangeChange = (dates: DateRangeValue) => {
    setDateRange(dates);

    setPagination((previous) => ({
      ...previous,
      current: 1,
    }));

    fetchMtsTransferLogs(1, pagination.pageSize, dates);
  };

  /* ---------------- PAGINATION ---------------- */

  const handleTableChange = (tablePagination: TablePaginationConfig) => {
    const current = tablePagination.current || 1;
    const pageSize = tablePagination.pageSize || 10;

    setPagination({
      current,
      pageSize,
    });

    fetchMtsTransferLogs(current, pageSize, dateRange);
  };

  const columns: ColumnsType<MtsTransferLog> = [
    {
      title: "ID",
      dataIndex: "id",
      key: "id",
      width: 80,
      fixed: "left",
    },
    {
      title: "User ID",
      dataIndex: "user_id",
      key: "user_id",
    },
    {
      title: "User Email",
      key: "user_email",
      render: (_, record) => record.user?.email || "-",
    },
    {
      title: "Wallet History ID",
      dataIndex: "wallet_terminal_history_id",
      key: "wallet_terminal_history_id",
    },
    {
      title: "Payment Type",
      dataIndex: "payment_type",
      key: "payment_type",
    },
    {
      title: "Request ID",
      dataIndex: "request_id",
      key: "request_id",
      width: 220,
      ellipsis: true,
    },
    {
      title: "Amount",
      dataIndex: "amount",
      key: "amount",
    },
    {
      title: "Currency",
      key: "currency",
      render: (_, record) => `${record.sender_currency}`,
    },
    // {
    //   title: "Status",
    //   dataIndex: "status",
    //   key: "status",
    // },
    {
      title: "Transfer Status",
      dataIndex: "transfer_status",
      key: "transfer_status",
      render: (value: string | null) => value || "-",
    },
    // {
    //   title: "MTS Status",
    //   key: "mts_status",
    //   render: (_, record) =>
    //     record.history?.mts_status_name ||
    //     record.response_data?.status?.name ||
    //     "-",
    // },
    {
      title: "MTS Ledger ID",
      dataIndex: "mts_ledger_id",
      key: "mts_ledger_id",
      render: (value: string | null) => value || "-",
    },
    // {
    //   title: "Error",
    //   dataIndex: "error_message",
    //   key: "error_message",
    //   render: (value: string | null) => value || "-",
    // },
    {
      title: "Created At",
      dataIndex: "created_at",
      key: "created_at",
      render: (value: string) =>
        value ? new Date(value).toLocaleString() : "-",
    },
  ];

  return (
    <div className="custom-scroll flex flex-col w-full bg-white rounded-[12px] p-4 mt-2 sm:mt-4">
      {!canViewMtsLogs ? (
        <div className="flex flex-col items-center justify-center p-8 text-center">
          <p className="font-manrope text-[16px] font-semibold text-secondary">
            You do not have permission to view MTS transfer logs.
          </p>
        </div>
      ) : (
        <>
          {/* Filters */}
          <div className="flex flex-wrap items-center gap-4 mb-5">
            <div className="flex gap-2">
              <Select<MtsTransferFilter>
                value={selectedFilter}
                onChange={handleFilterChange}
                className="min-w-[220px]"
                options={mtsTransferFilterOptions}
              />

              {selectedFilter === "status" ? (
                <Select<TransferStatus>
                  allowClear
                  value={filterValue ? (filterValue as TransferStatus) : undefined}
                  onChange={handleStatusChange}
                  placeholder="Select Transfer Status"
                  className="min-w-[220px]"
                  options={transferStatusOptions}
                />
              ) : (
                <input
                  type="text"
                  value={filterValue}
                  onChange={handleFilterValueChange}
                  placeholder={`Search by ${
                    mtsTransferFilterOptions.find(
                      (option) => option.value === selectedFilter,
                    )?.label || ""
                  }`}
                  className="h-[32px] min-w-[220px] border border-gray-300 rounded px-3 outline-none"
                />
              )}
            </div>

            <RangePicker
              value={dateRange}
              onChange={handleDateRangeChange}
              format="YYYY-MM-DD"
            />

            <Button onClick={handleClearFilters}>Clear Filters</Button>
          </div>

          <Table<MtsTransferLog>
            columns={columns}
            dataSource={dataSource}
            rowKey="id"
            loading={isLoading}
            scroll={{ x: "max-content" }}
            pagination={{
              current: pagination.current,
              pageSize: pagination.pageSize,
              total: totalCount,
              showTotal: (total) => `Total ${total} MTS transfer logs`,
              showSizeChanger: false,
            }}
            onChange={handleTableChange}
          />
        </>
      )}
    </div>
  );
};

export default MtsTransferLogs;
